import * as THREE from "three";
import { mergeGeometries } from "three/examples/jsm/utils/BufferGeometryUtils.js";

export interface PartTransform {
  position?: THREE.Vector3 | [number, number, number];
  rotation?: THREE.Euler | [number, number, number];
  scale?: THREE.Vector3 | [number, number, number] | number;
}

function toVector(value: PartTransform["position"], fallback: number): THREE.Vector3 {
  if (value === undefined) return new THREE.Vector3(fallback, fallback, fallback);
  if (Array.isArray(value)) return new THREE.Vector3(...value);
  return value.clone();
}

/**
 * Accumulates coloured, transformed geometry and bakes it into one buffer.
 *
 * Static level geometry is built once and drawn in a single call — far cheaper
 * than a mesh per tile, and it keeps the whole map in one object to dispose.
 */
export class GeoBuilder {
  private parts: THREE.BufferGeometry[] = [];

  get isEmpty(): boolean {
    return this.parts.length === 0;
  }

  add(geometry: THREE.BufferGeometry, color: number, transform: PartTransform = {}): this {
    // Three's primitives are a mix of indexed (box, sphere) and non-indexed
    // (cone, cylinder), and mergeGeometries refuses to mix the two. Normalising
    // to non-indexed costs a few vertices and makes every primitive mergeable.
    const source = geometry.index ? geometry.toNonIndexed() : geometry;
    const geo = source.clone();
    if (source !== geometry) source.dispose();

    const scale =
      typeof transform.scale === "number"
        ? new THREE.Vector3(transform.scale, transform.scale, transform.scale)
        : toVector(transform.scale, 1);
    const position = toVector(transform.position, 0);
    const rotation = Array.isArray(transform.rotation)
      ? new THREE.Euler(...transform.rotation)
      : (transform.rotation ?? new THREE.Euler());

    const matrix = new THREE.Matrix4().compose(
      position,
      new THREE.Quaternion().setFromEuler(rotation),
      scale,
    );
    geo.applyMatrix4(matrix);

    // Bake the colour into vertices so everything can share one material.
    const count = geo.attributes.position.count;
    const rgb = new THREE.Color(color);
    const colors = new Float32Array(count * 3);
    for (let i = 0; i < count; i++) {
      colors[i * 3] = rgb.r;
      colors[i * 3 + 1] = rgb.g;
      colors[i * 3 + 2] = rgb.b;
    }
    geo.setAttribute("color", new THREE.BufferAttribute(colors, 3));
    geo.deleteAttribute("uv");
    geo.clearGroups();

    this.parts.push(geo);
    return this;
  }

  /**
   * Like `add`, but tints the top face separately — the trick that makes flat
   * coloured blocks read as lit terrain.
   */
  addBlock(
    geometry: THREE.BufferGeometry,
    topColor: number,
    sideColor: number,
    transform: PartTransform = {},
  ): this {
    const before = this.parts.length;
    this.add(geometry, sideColor, transform);
    const geo = this.parts[before];

    const normal = geo.attributes.normal;
    const color = geo.attributes.color as THREE.BufferAttribute;
    const top = new THREE.Color(topColor);
    for (let i = 0; i < normal.count; i++) {
      if (normal.getY(i) > 0.5) color.setXYZ(i, top.r, top.g, top.b);
    }
    color.needsUpdate = true;
    return this;
  }

  /** Merges and clears. Returns null if nothing was added. */
  build(): THREE.BufferGeometry | null {
    if (this.parts.length === 0) return null;
    const merged = mergeGeometries(this.parts, false);
    for (const part of this.parts) part.dispose();
    this.parts = [];
    if (!merged) return null;
    merged.computeBoundingSphere();
    return merged;
  }

  /** Builds and wraps in a mesh with a vertex-coloured material. */
  buildMesh(options: THREE.MeshLambertMaterialParameters = {}): THREE.Mesh | null {
    const geo = this.build();
    if (!geo) return null;
    const mesh = new THREE.Mesh(
      geo,
      new THREE.MeshLambertMaterial({ vertexColors: true, ...options }),
    );
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    return mesh;
  }
}
